import { useState } from 'react'
import type { Site } from '@shared/types'
import { ApiError, post } from '../lib/api'
import { getCurrentFix } from '../lib/geo'
import { useToday } from '../state/today'
import { useToast } from '../state/toast'
import { Button, ErrorNote, Field, Sheet } from './ui'

interface Nearby {
  site: Site
  distanceM: number | null
}

/**
 * Anyone can add a site from wherever they pick one, so a new job never waits
 * on the office. Standing on it is the normal case — the phone's position
 * becomes the geofence. Not standing on it is fine too: the site goes in with
 * no location, and whoever is there first can fill it in.
 *
 * The mistake worth catching is a second copy of a site that already exists.
 * The server answers "that might be one of these" for anything within 300 m
 * or with the same name, and this sheet asks before creating it anyway.
 */
export function AddSiteSheet({ initialName = '', onClose, onPicked }: {
  initialName?: string
  onClose: () => void
  /** The new site, or an existing one the person said they meant. */
  onPicked: (site: Site) => void
}) {
  const { refresh } = useToday()
  const toast = useToast()
  const [name, setName] = useState(initialName)
  const [address, setAddress] = useState('')
  const [here, setHere] = useState(true)
  const [nearby, setNearby] = useState<Nearby[] | null>(null)
  const [fix, setFix] = useState<{ lat: number; lng: number } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const create = async (force: boolean) => {
    if (!name.trim()) return setError('Give the site a name.')
    setBusy(true)
    setError(null)
    try {
      let point = fix
      if (here && !point) {
        try {
          const current = await getCurrentFix({ timeout: 10_000 })
          point = { lat: current.lat, lng: current.lng }
          setFix(point)
        } catch (err) {
          setBusy(false)
          setHere(false)
          return setError(`${err instanceof Error ? err.message : 'No location'} Untick “I am at the site” to add it without one.`)
        }
      }
      const res = await post<{ site: Site }>('/api/sites', {
        name: name.trim(),
        address: address.trim() || undefined,
        lat: here ? point?.lat : undefined,
        lng: here ? point?.lng : undefined,
        force,
      })
      await refresh()
      toast.success(`${res.site.name} added. The office has been told.`)
      onPicked(res.site)
    } catch (err) {
      if (err instanceof ApiError && err.code === 'site_nearby') {
        setNearby((err.payload as { nearby?: Nearby[] } | null)?.nearby ?? [])
      } else {
        setError(err instanceof Error ? err.message : 'Could not add the site')
      }
    } finally {
      setBusy(false)
    }
  }

  if (nearby) {
    return (
      <Sheet
        open
        onClose={onClose}
        title="Is it one of these?"
        footer={
          <Button className="w-full" loading={busy} onClick={() => void create(true)}>
            No — add {name.trim()} as a new site
          </Button>
        }
      >
        <p className="mb-3 text-sm text-graphite-2">
          {nearby.some((n) => n.distanceM != null)
            ? 'There is already a site close to where you are standing.'
            : 'There is already a site with that name.'}
        </p>
        <div className="space-y-2">
          {nearby.map(({ site, distanceM }) => (
            <button
              key={site.id}
              type="button"
              onClick={() => onPicked(site)}
              className="card flex w-full items-center gap-3 p-3 text-left"
            >
              <span className="size-3 shrink-0 rounded-full" style={{ background: site.color }} aria-hidden />
              <span className="min-w-0 flex-1">
                <span className="block truncate font-bold">{site.name}</span>
                {site.address && <span className="block truncate text-xs text-graphite-2">{site.address}</span>}
              </span>
              <span className="shrink-0 text-xs font-bold">
                {distanceM != null ? `${distanceM} m · ` : ''}Use this
              </span>
            </button>
          ))}
        </div>
        <ErrorNote>{error}</ErrorNote>
      </Sheet>
    )
  }

  return (
    <Sheet
      open
      onClose={onClose}
      title="Add a new site"
      footer={
        <Button variant="primary" size="lg" className="w-full" loading={busy} onClick={() => void create(false)}>
          Add site
        </Button>
      }
    >
      <div className="space-y-4">
        <Field label="Site name" required>
          <input
            className="field"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Henderson reno"
          />
        </Field>
        <Field label="Address" hint="Optional">
          <input
            className="field"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="42 Concession Rd 6"
          />
        </Field>
        <label className="flex min-h-12 items-center gap-3">
          <input
            type="checkbox"
            className="size-5 shrink-0 accent-graphite"
            checked={here}
            onChange={(e) => setHere(e.target.checked)}
          />
          <span className="text-sm">
            <span className="font-bold">I am at the site</span>
            <span className="block text-xs text-graphite-2">
              {here
                ? 'Your location becomes the site’s location, so the app knows when you arrive.'
                : 'The site is added with no location. Anyone standing on it can add one later.'}
            </span>
          </span>
        </label>
        <ErrorNote>{error}</ErrorNote>
      </div>
    </Sheet>
  )
}

/** Sentinel option value for "add a new site" inside a native select. */
export const NEW_SITE = '__new_site__'

/**
 * A native site <select> with "Add a new site…" as its last option. Native
 * keeps the phone's own picker, which is what gloved thumbs are used to.
 */
export function SiteSelect({ value, onChange, sites, emptyLabel, className = '' }: {
  value: string
  onChange: (siteId: string) => void
  sites: { id: string; name: string }[]
  /** Label for the "no site" option; omit to not offer one. */
  emptyLabel?: string
  className?: string
}) {
  const [adding, setAdding] = useState(false)
  return (
    <>
      <select
        className={`field ${className}`}
        value={value}
        onChange={(e) => {
          if (e.target.value === NEW_SITE) setAdding(true)
          else onChange(e.target.value)
        }}
      >
        {emptyLabel !== undefined && <option value="">{emptyLabel}</option>}
        {sites.map((site) => <option key={site.id} value={site.id}>{site.name}</option>)}
        <option value={NEW_SITE}>+ Add a new site…</option>
      </select>
      {adding && (
        <AddSiteSheet
          onClose={() => setAdding(false)}
          onPicked={(site) => {
            setAdding(false)
            onChange(site.id)
          }}
        />
      )}
    </>
  )
}
